import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatErr } from "../lib/api";
import { ScareLogo } from "../components/Brand";
import { PasswordStrengthBar, passwordStrength } from "../components/AuthBits";
import { Eye, EyeOff, CheckCircle2 } from "lucide-react";

export default function ResetPasswordPage() {
  const { resetPassword } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const valid = passwordStrength(password).valid;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) { setErr("Please choose a stronger password."); return; }
    setErr(""); setLoading(true);
    try { await resetPassword(token, password); setDone(true); setTimeout(() => nav("/login", { replace: true }), 1500); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-md st-card p-10 st-fade-up" data-testid="reset-page">
        <div className="flex justify-center mb-6"><ScareLogo size={36} /></div>
        {done ? (
          <div className="text-center" data-testid="reset-success">
            <CheckCircle2 size={40} className="mx-auto text-[#22c55e]" />
            <h1 className="text-2xl font-bold mt-4">Password updated</h1>
            <p className="text-[#bbb] mt-2">Redirecting you to sign in…</p>
          </div>
        ) : !token ? (
          <p className="text-center text-[#fca5a5]" data-testid="reset-no-token">Invalid reset link. <Link to="/forgot-password" className="st-link">Request a new one</Link>.</p>
        ) : (
          <>
            <h1 className="text-2xl font-bold mb-1">Set a new password</h1>
            <p className="text-[#8a8a8a] mb-6">Choose a strong password for your account.</p>
            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <div className="relative">
                <input data-testid="reset-password" className="st-input pr-10" type={show ? "text" : "password"} required
                       value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-3 text-[#8a8a8a] hover:text-white" aria-label="Toggle password">
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <PasswordStrengthBar value={password} />
              {err && <div data-testid="reset-error" className="text-sm text-[#fca5a5] bg-[#2a1212] border border-[#5b1919] rounded-lg p-3">{err}</div>}
              <button data-testid="reset-submit" disabled={loading} className="st-btn w-full justify-center">{loading ? "Updating…" : "Update password"}</button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
